import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, ZoomIn, ZoomOut } from "lucide-react";

async function getCroppedBlob(imageSrc, cropPixels) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cropPixels.width));
  canvas.height = Math.max(1, Math.round(cropPixels.height));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, canvas.width, canvas.height
  );
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.92));
}

export default function ImageCropModal({ src, aspect = 1, busy = false, onCancel, onApply }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);

  const onComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const apply = async () => {
    if (!pixels) return;
    const blob = await getCroppedBlob(src, pixels);
    await onApply(blob);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center" data-testid="image-crop-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-amber-900/10">
          <h3 className="font-display text-lg font-bold text-maroon">Crop &amp; Zoom Photo</h3>
          <button onClick={onCancel} data-testid="crop-close"><X /></button>
        </div>
        <div className="relative w-full h-80 bg-slate-900">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
            showGrid
          />
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)))} className="w-9 h-9 grid place-items-center rounded-lg border border-amber-900/15 text-maroon hover:bg-warm-surface" data-testid="crop-zoom-out"><ZoomOut size={16} /></button>
            <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-amber-600" data-testid="crop-zoom-slider" aria-label="Zoom" />
            <button type="button" onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))} className="w-9 h-9 grid place-items-center rounded-lg border border-amber-900/15 text-maroon hover:bg-warm-surface" data-testid="crop-zoom-in"><ZoomIn size={16} /></button>
          </div>
          <p className="text-xs text-slate-500 text-center">Drag the photo to reposition, and use the slider to zoom.</p>
          <div className="flex justify-end gap-3">
            <button onClick={onCancel} className="h-10 px-5 rounded-full font-semibold text-slate-700 hover:bg-warm-surface" data-testid="crop-cancel">Cancel</button>
            <button onClick={apply} disabled={busy || !pixels} className="btn-gold h-10 px-6 rounded-full font-semibold min-w-[120px] disabled:opacity-60" data-testid="crop-apply">{busy ? "Saving…" : "Apply"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
